import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import Login from './components/Login';
import AdminLogin from './components/AdminLogin';
import Dashboard from './components/Dashboard';
import Users from './components/Users';
import AdminEvents from './components/AdminEvents';
import Batches from './components/Batches';
import Photos from './components/Photos';
import Verifications from './components/Verifications';
import Logs from './components/Logs';
import Deletions from './components/Deletions';
import Storage from './components/Storage';
import Notifications from './components/Notifications';
import SubscriptionPlans from './components/SubscriptionPlans';
import PendingSubscriptions from './components/PendingSubscriptions';
import AdminPayments from './components/AdminPayments';
import Admins from './components/Admins';
import Analytics from './components/Analytics';
import Layout from './components/Layout';
import UserLayout from './components/UserLayout';
import UserDashboard from './components/UserDashboard';
import UserEvents from './components/UserEvents';
import UserEventDetail from './components/UserEventDetail';
import UserBatches from './components/UserBatches';
import UserSubscription from './components/UserSubscription';
import UserProfile from './components/UserProfile';
import UserVerifications from './components/UserVerifications';
import UserAnalytics from './components/UserAnalytics';
import Help from './components/Help';
import AdminOnly from './components/AdminOnly';
import UploadPage from './components/UploadPage';
import VerifyPage from './components/VerifyPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BackgroundJobsProvider } from './context/BackgroundJobsContext';
import BackgroundJobsProgress from './components/BackgroundJobsProgress';
import CompletionToasts from './components/CompletionToasts';

const ADMIN_PATHS = ['/users', '/storage', '/events', '/photos', '/logs', '/deletions', '/subscription-plans', '/subscriptions', '/payments', '/admins'];

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  if (!isAuthenticated) {
    const isAdminRoute = ADMIN_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'));
    return <Navigate to={isAdminRoute ? '/admin' : '/login'} replace />;
  }
  return children;
};

const LayoutSwitch = () => {
  const { userType } = useAuth();
  return userType === 'user' ? <UserLayout><Outlet /></UserLayout> : <Layout><Outlet /></Layout>;
};

const DashboardSwitch = () => {
  const { userType } = useAuth();
  return userType === 'user' ? <UserDashboard /> : <Dashboard />;
};

const BatchesSwitch = () => {
  const { userType } = useAuth();
  return userType === 'user' ? <UserBatches /> : <Batches />;
};

const EventsSwitch = () => {
  const { userType } = useAuth();
  return userType === 'user' ? <UserEvents /> : <AdminOnly><AdminEvents /></AdminOnly>;
};

const UserSubscriptionRoute = () => {
  const { userType } = useAuth();
  if (userType !== 'user') return <Navigate to="/" replace />;
  return <UserSubscription />;
};

const UserProfileRoute = () => {
  const { userType } = useAuth();
  if (userType !== 'user') return <Navigate to="/" replace />;
  return <UserProfile />;
};

const VerificationsSwitch = () => {
  const { userType } = useAuth();
  return userType === 'user' ? <UserVerifications /> : <AdminOnly><Verifications /></AdminOnly>;
};

const AnalyticsSwitch = () => {
  const { userType } = useAuth();
  return userType === 'user' ? <UserAnalytics /> : <AdminOnly><Analytics /></AdminOnly>;
};

const UserEventDetailRoute = () => {
  const { userType } = useAuth();
  if (userType !== 'user') return <Navigate to="/events" replace />;
  return <UserEventDetail />;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <BackgroundJobsProvider>
          <>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/admin" element={<AdminLogin />} />
              <Route path="/upload/:token" element={<UploadPage />} />
              <Route path="/verify/:token" element={<VerifyPage />} />
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <LayoutSwitch />
                  </PrivateRoute>
                }
              >
                <Route index element={<DashboardSwitch />} />
                <Route path="profile" element={<UserProfileRoute />} />
                <Route path="subscription" element={<UserSubscriptionRoute />} />
                <Route path="users" element={<AdminOnly><Users /></AdminOnly>} />
                <Route path="admins" element={<AdminOnly><Admins /></AdminOnly>} />
                <Route path="verifications" element={<VerificationsSwitch />} />
                <Route path="analytics" element={<AnalyticsSwitch />} />
                <Route path="storage" element={<AdminOnly><Storage /></AdminOnly>} />
                <Route path="events" element={<EventsSwitch />} />
                <Route path="events/:id" element={<UserEventDetailRoute />} />
                <Route path="batches" element={<BatchesSwitch />} />
                <Route path="photos" element={<AdminOnly><Photos /></AdminOnly>} />
                <Route path="logs" element={<AdminOnly><Logs /></AdminOnly>} />
                <Route path="deletions" element={<AdminOnly><Deletions /></AdminOnly>} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="subscription-plans" element={<AdminOnly><SubscriptionPlans /></AdminOnly>} />
                <Route path="subscriptions" element={<AdminOnly><PendingSubscriptions /></AdminOnly>} />
                <Route path="payments" element={<AdminOnly><AdminPayments /></AdminOnly>} />
                <Route path="help" element={<Help />} />
              </Route>
            </Routes>
            <CompletionToasts />
            <BackgroundJobsProgress />
          </>
        </BackgroundJobsProvider>
      </Router>
    </AuthProvider>
  );
}

export default App;

